import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { baseURL } from '../config';
import AuthContext from '../Context/AuthContext';
import InputField from './InputField';
import TextField from './TextField';
import ButtonSolid from './ButtonSolid';
import ButtonOutline from './ButtonOutline';
import OverlayLoading from './OverlayLoading';
import Popup from './Popup';
import AssemblySelector from './AssemblySelector';
import AssetSelector from './AssetSelector';
import AssemblyTypeAutocomplete from './AssemblyTypeAutocomplete';
import { AssemblyFormValidation } from '../utils/AssemblyFormValidation';
import { canHoldAssetType } from '../utils/assetTypeRules';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';

const emptyAssemblyData = {
    assembly_number: '',
    assembly_description: '',
    assembly_type: '',
};

const MAX_EMBEDDED_ASSEMBLIES = 8;
const MAX_ELECTRIC_SCHEMATICS = 3;
const MAX_PNEUMATIC_SCHEMATIC = 1;
const CONTROL_PACKAGE_TYPE = 'Control Package';

const TABS = [
    { key: 'basic', label: 'Basic Details' },
    { key: 'embedded', label: 'Embedded Assemblies' },
    { key: 'electrical', label: 'Electrical Schematics', controlPackageOnly: true },
    { key: 'pneumatic', label: 'Pneumatic Schematic', controlPackageOnly: true },
    { key: 'assets', label: 'Assets' },
];

function AssemblyForm({ editAssembly, assembly_id, initialData, fetchingAssemblyData, savingAssembly, onSave, onSaved, onReload, assemblyTypeOptions }) {
    const navigate = useNavigate();
    const { token, logout } = useContext(AuthContext);

    const [assemblyData, setAssemblyData] = useState(emptyAssemblyData);
    const [embeddedAssemblies, setEmbeddedAssemblies] = useState([]);
    const [addedEmbedded, setAddedEmbedded] = useState([]);
    const [deletedEmbedded, setDeletedEmbedded] = useState([]);
    const [electricSchematics, setElectricSchematics] = useState([]);
    const [addedSchematic, setAddedSchematic] = useState([]);
    const [deletedSchematic, setDeletedSchematic] = useState([]);
    const [pneumaticSchematic, setPneumaticSchematic] = useState(null);
    const [pneumaticChanged, setPneumaticChanged] = useState(false);
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [addedAssets, setAddedAssets] = useState([]);
    const [deletedAssets, setDeletedAssets] = useState([]);
    const [validateFields, setValidateFields] = useState({});
    const [fetchingAssets, setFetchingAssets] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    const [generatingPackage, setGeneratingPackage] = useState(false);
    const [showRegenPrompt, setShowRegenPrompt] = useState(false);

    // The control package is built from the PDFs of the linked embedded, electrical
    // and pneumatic assemblies, so it only makes sense for a Control Package with some.
    const isControlPackage = assemblyData.assembly_type === CONTROL_PACKAGE_TYPE;
    // The schematic tabs only apply to Control Packages; fall back to the first tab if the open one goes away.
    const visibleTabs = TABS.filter((tab) => !tab.controlPackageOnly || isControlPackage);
    const currentTab = visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : 'basic';
    const hasLinkedAssemblies = embeddedAssemblies.length + electricSchematics.length + (pneumaticSchematic ? 1 : 0) > 0;
    const canGeneratePackage = isControlPackage && hasLinkedAssemblies;
    const linkedAssembliesChanged = addedEmbedded.length + deletedEmbedded.length + addedSchematic.length + deletedSchematic.length > 0
        || pneumaticChanged
        || (!!initialData && initialData.assembly_type !== CONTROL_PACKAGE_TYPE);
    const hasUnsavedChanges = !!initialData && (
        assemblyData.assembly_number !== (initialData.assembly_number || '')
        || assemblyData.assembly_description !== (initialData.assembly_description || '')
        || assemblyData.assembly_type !== (initialData.assembly_type || '')
        || addedEmbedded.length + deletedEmbedded.length + addedSchematic.length + deletedSchematic.length + addedAssets.length + deletedAssets.length > 0
        || pneumaticChanged
    );
    // On add there is nothing to compare against, so anything filled in counts.
    const { allowNavigation } = useUnsavedChanges(editAssembly
        ? hasUnsavedChanges
        : Boolean(
            assemblyData.assembly_number
            || assemblyData.assembly_description
            || assemblyData.assembly_type
            || embeddedAssemblies.length
            || electricSchematics.length
            || pneumaticSchematic
            || selectedAssets.length
        ));
    const regenerateBlockedReason = hasUnsavedChanges
        ? 'Save your changes first - the package is built from the saved assembly.'
        : !hasLinkedAssemblies
            ? 'Link embedded, electrical or pneumatic assemblies first.'
            : '';

    useEffect(() => {
        if (!initialData) return;
        setAssemblyData({
            assembly_number: initialData.assembly_number || '',
            assembly_description: initialData.assembly_description || '',
            assembly_type: initialData.assembly_type || '',
        });
        setEmbeddedAssemblies(initialData.embedded_assemblies || []);
        setElectricSchematics(initialData.electric_schematic || []);
        setPneumaticSchematic(initialData.pneumatic_schematic || null);
        setAddedEmbedded([]);
        setDeletedEmbedded([]);
        setAddedSchematic([]);
        setDeletedSchematic([]);
        setPneumaticChanged(false);
        setAddedAssets([]);
        setDeletedAssets([]);

        const fetchAssetDetails = async (assetIds) => {
            try {
                setFetchingAssets(true);
                const response = await axios.get(`${baseURL}/asset/getAssets`, {
                    headers: { 'ngrok-skip-browser-warning': 'any', 'x-access-token': token },
                    params: { asset_ids: assetIds },
                });
                setSelectedAssets(response.data.data.assetData);
            } catch (error) {
                if (error.response?.status === 401) logout();
            } finally {
                setFetchingAssets(false);
            }
        };

        if (initialData.assets?.length > 0) {
            fetchAssetDetails(initialData.assets);
        } else {
            setSelectedAssets([]);
        }
    }, [initialData, token, logout]);

    const handleFieldChange = (field, value) => {
        setAssemblyData((prev) => ({ ...prev, [field]: value }));
    };

    const addEmbedded = (item) => {
        setEmbeddedAssemblies((prev) => [...prev, item]);
        setAddedEmbedded((prev) => [...prev, item._id]);
        setDeletedEmbedded((prev) => prev.filter((id) => id !== item._id));
    };

    const removeEmbedded = (item) => {
        setEmbeddedAssemblies((prev) => prev.filter((a) => a._id !== item._id));
        setAddedEmbedded((prev) => prev.filter((id) => id !== item._id));
        if (initialData?.embedded_assemblies?.some((a) => a._id === item._id)) {
            setDeletedEmbedded((prev) => [...prev, item._id]);
        }
    };

    const addSchematic = (item) => {
        setElectricSchematics((prev) => [...prev, item]);
        setAddedSchematic((prev) => [...prev, item._id]);
        setDeletedSchematic((prev) => prev.filter((id) => id !== item._id));
    };

    const removeSchematic = (item) => {
        setElectricSchematics((prev) => prev.filter((a) => a._id !== item._id));
        setAddedSchematic((prev) => prev.filter((id) => id !== item._id));
        if (initialData?.electric_schematic?.some((a) => a._id === item._id)) {
            setDeletedSchematic((prev) => [...prev, item._id]);
        }
    };

    const addPneumatic = (item) => {
        setPneumaticSchematic(item);
        setPneumaticChanged(true);
    };

    const removePneumatic = () => {
        setPneumaticSchematic(null);
        setPneumaticChanged(true);
    };

    const addAsset = (item) => {
        // The same asset can come back more than once (e.g. re-uploading an identical
        // file resolves to the existing asset) - never list it twice on an assembly.
        if (selectedAssets.some((a) => a._id === item._id)) return;
        setSelectedAssets((prev) => [...prev, item]);
        setAddedAssets((prev) => [...prev, item._id]);
        setDeletedAssets((prev) => prev.filter((id) => id !== item._id));
    };

    const removeAsset = (item) => {
        setSelectedAssets((prev) => prev.filter((a) => a._id !== item._id));
        setAddedAssets((prev) => prev.filter((id) => id !== item._id));
        if (initialData?.assets?.includes(item._id)) {
            setDeletedAssets((prev) => [...prev, item._id]);
        }
    };

    const replaceAsset = (updatedAsset) => {
        setSelectedAssets((prev) => prev.map((a) => (a._id === updatedAsset._id ? updatedAsset : a)));
    };

    // Returns null on success, otherwise the message to show.
    const requestControlPackage = async (targetAssemblyId) => {
        try {
            await axios.post(`${baseURL}/assembly/generateControlPackage/${targetAssemblyId}`, {}, {
                headers: { 'ngrok-skip-browser-warning': 'any', 'x-access-token': token },
            });
            return null;
        } catch (error) {
            if (error.response?.status === 401) logout();
            return error.response?.data?.message || error.message || 'Something went wrong';
        }
    };

    // Manual re-generate (edit page) - e.g. after the automatic one failed.
    const handleRegenerate = async () => {
        setGeneratingPackage(true);
        const errorMessage = await requestControlPackage(assembly_id);
        setGeneratingPackage(false);
        if (errorMessage) {
            toast.error(`Control package could not be generated: ${errorMessage}`, { duration: 8000 });
            return;
        }
        toast.success('Control package generated successfully');
        if (onReload) onReload();
    };

    const handleSave = async () => {
        const [isAnyEmptyFields, validateFieldsCopy] = AssemblyFormValidation(assemblyData, validateFields);
        setValidateFields(validateFieldsCopy);
        if (isAnyEmptyFields) {
            setActiveTab('basic');
            return;
        }

        // On edit, a change to what feeds the package is the moment to offer rebuilding it.
        if (editAssembly && canGeneratePackage && linkedAssembliesChanged) {
            setShowRegenPrompt(true);
            return;
        }
        await saveAssembly(!editAssembly && canGeneratePackage);
    };

    const saveAssembly = async (generatePackage) => {
        setShowRegenPrompt(false);
        const payload = { ...assemblyData };
        // Schematic selections only count for Control Packages, and an asset only counts if
        // this assembly type can hold it - for anything else the tab is hidden, so what was
        // picked there earlier is not submitted.
        const holdsAsset = (asset) => asset.type !== CONTROL_PACKAGE_TYPE && canHoldAssetType(assemblyData.assembly_type, asset.type);
        const submittableAssetIds = (ids) => ids.filter((id) => {
            const asset = selectedAssets.find((a) => a._id === id);
            return !asset || holdsAsset(asset);
        });
        if (editAssembly) {
            payload.add_embedded_assemblies = addedEmbedded;
            payload.delete_embedded_assemblies = deletedEmbedded;
            payload.add_electric_schematic = isControlPackage ? addedSchematic : [];
            payload.delete_electric_schematic = isControlPackage ? deletedSchematic : [];
            payload.add_assets = submittableAssetIds(addedAssets);
            payload.delete_assets = deletedAssets;
            if (isControlPackage && pneumaticChanged) {
                payload.pneumatic_schematic = pneumaticSchematic?._id || '';
            }
        } else {
            payload.embedded_assemblies = embeddedAssemblies.map((a) => a._id);
            payload.electric_schematic = isControlPackage ? electricSchematics.map((a) => a._id) : [];
            payload.assets = selectedAssets.filter(holdsAsset).map((a) => a._id);
            if (isControlPackage && pneumaticSchematic) {
                payload.pneumatic_schematic = pneumaticSchematic._id;
            }
        }

        const savedAssemblyId = await onSave(payload);
        if (!savedAssemblyId) return;

        // The assembly is saved either way - a failed package only shows an error.
        let packageError = null;
        if (generatePackage) {
            setGeneratingPackage(true);
            packageError = await requestControlPackage(savedAssemblyId);
            setGeneratingPackage(false);
        }
        allowNavigation();
        onSaved(savedAssemblyId);
        if (generatePackage) {
            if (packageError) {
                toast.error(`Assembly saved, but the control package could not be generated: ${packageError}`, { duration: 8000 });
            } else {
                toast.success('Control package generated successfully');
            }
        }
    };

    return (
        <div className='py-3 md:py-6'>
            <div className='sticky top-0 z-10 bg-gray-100 flex flex-wrap justify-between items-center gap-4 py-3'>
                <p className='text-lg font-medium md:text-xl md:font-semibold'>
                    {editAssembly ? 'Edit Assembly' : 'Add Assembly'}
                </p>
                <div className='flex gap-4'>
                    <ButtonOutline buttonText='Cancel' onClick={() => navigate(-1)} disabled={savingAssembly} />
                    <ButtonSolid
                        buttonText={editAssembly ? 'Save Changes' : 'Create Assembly'}
                        onClick={handleSave}
                        disabled={fetchingAssemblyData || savingAssembly}
                        showAnimation={savingAssembly}
                        loadingText={editAssembly ? 'Saving...' : 'Creating...'}
                    />
                </div>
            </div>

            <div className='flex flex-wrap gap-2 border-b border-gray-200 mb-5'>
                {visibleTabs.map((tab) => (
                    <button
                        key={tab.key}
                        type='button'
                        onClick={() => setActiveTab(tab.key)}
                        className={`px-3 md:px-4 py-2 text-sm md:text-base font-medium border-b-2 -mb-px ${currentTab === tab.key
                            ? 'border-orange-400 text-orange-500'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {currentTab === 'basic' && (
                <div className='bg-white shadow-md rounded-lg p-4 md:p-6 mb-5'>
                    <p className='text-xl font-medium text-gray-700 mb-4'>Assembly Configuration</p>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                        <div>
                            <InputField
                                labelId='assembly_number'
                                labelText={<>Assembly # <span className='text-red-600'>*</span></>}
                                value={assemblyData.assembly_number}
                                disabled={fetchingAssemblyData}
                                onChange={(e) => handleFieldChange('assembly_number', e.target.value)}
                                errorMessage={validateFields.assembly_number}
                            />
                        </div>
                        <AssemblyTypeAutocomplete
                            id='assembly_type'
                            labelText={<>Assembly Type <span className='text-red-600'>*</span></>}
                            value={assemblyData.assembly_type}
                            options={assemblyTypeOptions}
                            disabled={fetchingAssemblyData}
                            onValueChange={(value) => handleFieldChange('assembly_type', value)}
                            errorMessage={validateFields.assembly_type}
                        />
                    </div>
                    <TextField
                        labelId='assembly_description'
                        labelText='Assembly Description'
                        value={assemblyData.assembly_description}
                        disabled={fetchingAssemblyData}
                        onChange={(e) => handleFieldChange('assembly_description', e.target.value)}
                    />
                </div>
            )}

            {currentTab === 'embedded' && (
                <AssemblySelector
                    label='Embedded Assemblies'
                    selected={embeddedAssemblies}
                    onAdd={addEmbedded}
                    onRemove={removeEmbedded}
                    maxSelect={MAX_EMBEDDED_ASSEMBLIES}
                    excludeId={assembly_id}
                    disabled={fetchingAssemblyData}
                />
            )}

            {currentTab === 'electrical' && (
                <AssemblySelector
                    label='Electrical Schematics'
                    selected={electricSchematics}
                    onAdd={addSchematic}
                    onRemove={removeSchematic}
                    maxSelect={MAX_ELECTRIC_SCHEMATICS}
                    excludeId={assembly_id}
                    disabled={fetchingAssemblyData}
                />
            )}

            {currentTab === 'pneumatic' && (
                <AssemblySelector
                    label='Pneumatic Schematic'
                    selected={pneumaticSchematic ? [pneumaticSchematic] : []}
                    onAdd={addPneumatic}
                    onRemove={removePneumatic}
                    maxSelect={MAX_PNEUMATIC_SCHEMATIC}
                    excludeId={assembly_id}
                    disabled={fetchingAssemblyData}
                />
            )}

            {currentTab === 'assets' && (
                <AssetSelector
                    label='Assets'
                    selected={selectedAssets}
                    onAdd={addAsset}
                    onRemove={removeAsset}
                    onReplaced={replaceAsset}
                    assemblyType={assemblyData.assembly_type}
                    allowControlPackage={editAssembly && isControlPackage}
                    onRegenerateControlPackage={editAssembly ? handleRegenerate : undefined}
                    regenerateBlockedReason={regenerateBlockedReason}
                    disabled={fetchingAssemblyData || fetchingAssets}
                />
            )}

            {showRegenPrompt && (
                <Popup maxWidth='md:max-w-[500px]'>
                    <div className='p-2'>
                        <p className='text-lg font-semibold text-gray-700 mb-2'>Re-generate control package?</p>
                        <p className='text-sm text-gray-600 mb-5'>
                            The assemblies feeding this control package have changed. You can rebuild the control package PDF while saving, or save and keep the current one as it is.
                        </p>
                        <div className='flex flex-col gap-3'>
                            <ButtonSolid buttonText='Save & Re-generate' onClick={() => saveAssembly(true)} className='w-full' />
                            <ButtonOutline buttonText='Save without re-generating' onClick={() => saveAssembly(false)} className='w-full' />
                            <button
                                type='button'
                                onClick={() => setShowRegenPrompt(false)}
                                className='text-sm text-gray-500 hover:text-gray-700 py-2'
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </Popup>
            )}

            {generatingPackage && <OverlayLoading loadingText='Generating control package...' />}
        </div>
    );
}

export default AssemblyForm;
